import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SearchIcon from "@mui/icons-material/Search";
import { useFormik } from "formik";
import * as Yup from "yup";
import { searchLeads } from "../../services/lead.service";

type UserData = {
  id?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  zipcode?: string;
};

type AddUserModalProps = {
  open: boolean;
  handleClose: (action?: string) => void;
  businessId: string;
  editMode?: boolean;
  userData?: UserData;
  title: string;
  createUser: (data: any) => Promise<any>;
  updateUser: (id: string, data: any) => Promise<any>;
  successMessages: {
    add: string;
    edit: string;
  };
  showLeadSearch?: boolean;
};

const AddUserModal = ({
  open,
  handleClose,
  businessId,
  editMode = false,
  userData,
  title,
  createUser,
  updateUser,
  successMessages,
  showLeadSearch = false,
}: AddUserModalProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [alertSeverity, setAlertSeverity] = useState<
    "success" | "error" | "info"
  >("success");

  const [leadSearchQuery, setLeadSearchQuery] = useState("");
  const [leadSearchResults, setLeadSearchResults] = useState<any[]>([]);
  const [leadSearchLoading, setLeadSearchLoading] = useState(false);
  const [leadSearchAnchorEl, setLeadSearchAnchorEl] = useState<null | HTMLElement>(null);

  const handleLeadSearch = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setLeadSearchResults([]);
      return;
    }
    setLeadSearchLoading(true);
    try {
      const leads = await searchLeads(query, businessId);
      setLeadSearchResults(leads?.data || []);
    } catch (error) {
      console.error("Search leads error:", error);
      setLeadSearchResults([]);
    } finally {
      setLeadSearchLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (showLeadSearch && leadSearchQuery) {
        handleLeadSearch(leadSearchQuery);
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [leadSearchQuery, showLeadSearch, handleLeadSearch]);

  const handleLeadSelect = (lead: any) => {
    setLeadSearchQuery(lead.fullName || `${lead.firstName} ${lead.lastName}`);
    setLeadSearchAnchorEl(null);
    formik.setValues({
      firstName: lead.firstName || "",
      middleName: lead.middleName || "",
      lastName: lead.lastName || "",
      email: lead.email || "",
      phone: lead.phone || "",
      password: "",
      confirmPassword: "",
      street: lead.street || "",
      city: lead.city || "",
      state: lead.state || "",
      zipcode: lead.zipcode || "",
    });
  };

  const handleLeadSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLeadSearchQuery(event.target.value);
    setLeadSearchAnchorEl(event.currentTarget);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      street: "",
      city: "",
      state: "",
      zipcode: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .min(3, "First name must be at least 3 characters")
        .required("First name is required"),
      middleName: Yup.string().optional(),
      lastName: Yup.string()
        .min(3, "Last name must be at least 3 characters")
        .required("Last name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string()
        .matches(/^\d{10}$/, "Phone must be exactly 10 digits")
        .required("Phone is required"),
      password: editMode
        ? Yup.string()
        : Yup.string().required("Password is required"),
      confirmPassword: editMode
        ? Yup.string().oneOf([Yup.ref("password")], "Passwords must match")
        : Yup.string()
            .oneOf([Yup.ref("password")], "Passwords must match")
            .required("Confirm Password is required"),
      street: Yup.string().optional(),
      city: Yup.string().optional(),
      state: Yup.string().optional(),
      zipcode: Yup.string().optional(),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const user: any = {
          ...values,
          businessId: businessId,
          isActive: true,
        };
        if (editMode && userData?.id) {
          delete user.password;
          delete user.confirmPassword;
          await updateUser(userData.id, user);
          setAlertSeverity("success");
          setSnackbar({
            open: true,
            message: successMessages.edit,
            severity: "success",
          });
        } else {
          await createUser(user);
          setAlertSeverity("success");
          setSnackbar({
            open: true,
            message: successMessages.add,
            severity: "success",
          });
        }
        handleClose("reload");
        formik.resetForm();
      } catch (error: any) {
        setAlertSeverity("error");
        setSnackbar({
          open: true,
          message: error?.message || "Operation failed.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (open) {
      if (editMode && userData) {
        formik.setValues({
          firstName: userData.firstName || "",
          middleName: userData.middleName || "",
          lastName: userData.lastName || "",
          email: userData.email || "",
          phone: userData.phone || "",
          password: "",
          confirmPassword: "",
          street: userData.street || "",
          city: userData.city || "",
          state: userData.state || "",
          zipcode: userData.zipcode || "",
        });
      } else if (!editMode && userData && userData.firstName) {
        formik.setValues({
          firstName: userData.firstName || "",
          middleName: userData.middleName || "",
          lastName: userData.lastName || "",
          email: userData.email || "",
          phone: userData.phone || "",
          password: "",
          confirmPassword: "",
          street: userData.street || "",
          city: userData.city || "",
          state: userData.state || "",
          zipcode: userData.zipcode || "",
        });
      } else {
        formik.resetForm();
      }
      setLeadSearchQuery("");
      setLeadSearchResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editMode, userData]);

  const openLeadSearch = Boolean(leadSearchAnchorEl) && leadSearchResults.length > 0;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose("cancel");
        }
      }}
      fullScreen={useMediaQuery(useTheme().breakpoints.down("sm"))}
      maxWidth={useMediaQuery(useTheme().breakpoints.down("sm")) ? false : "md"}
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          height: useMediaQuery(useTheme().breakpoints.down("sm")) ? "100vh" : "auto",
          maxHeight: useMediaQuery(useTheme().breakpoints.down("sm")) ? "100vh" : "none",
          m: useMediaQuery(useTheme().breakpoints.down("sm")) ? 0 : undefined,
          borderRadius: useMediaQuery(useTheme().breakpoints.down("sm")) ? 0 : undefined,
        },
      }}
    >

      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" component="span" className="font-bold text-gray-800">
          {editMode ? `Edit ${title}` : `Add ${title}`}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => handleClose("cancel")}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <form onSubmit={formik.handleSubmit}>
        <DialogContent
          className="custom-scrollbar"
          sx={{ flex: 1, overflowY: "auto" }}
        >
          <div className="space-y-4 py-2">
            <Grid container spacing={2}>

              {showLeadSearch && !editMode && !userData?.firstName && (
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      fullWidth
                      label="Search Existing Lead"
                      value={leadSearchQuery}
                      onChange={handleLeadSearchChange}
                      onFocus={(e) => setLeadSearchAnchorEl(e.currentTarget)}
                      placeholder="Search by name, email, or phone"
                      InputProps={{
                        style: { borderRadius: 10 },
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: leadSearchLoading ? (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                    <Popper
                      open={openLeadSearch}
                      anchorEl={leadSearchAnchorEl}
                      placement="bottom-start"
                      style={{ zIndex: 1300, width: leadSearchAnchorEl ? leadSearchAnchorEl.clientWidth : "auto" }}
                    >
                      <Paper elevation={3} sx={{ maxHeight: 300, overflow: "auto" }}>
                        <List dense>
                          {leadSearchResults.map((lead) => (
                            <ListItem key={lead.id} disablePadding>
                              <ListItemButton onClick={() => handleLeadSelect(lead)}>
                                <ListItemText
                                  primary={lead.fullName || `${lead.firstName} ${lead.lastName}`}
                                  secondary={`${lead.email || "-"} | ${lead.phone || "-"}`}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Popper>
                  </Box>
                </Grid>
              )}
            <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="First Name *"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter first name"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Middle Name (optional)"
              name="middleName"
              value={formik.values.middleName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.middleName && Boolean(formik.errors.middleName)}
              helperText={formik.touched.middleName && formik.errors.middleName}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter middle name"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Last Name *"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter last name"
            />
            </Grid>
           <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Email *"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter email address"
            />
           </Grid>
           <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Phone *"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter phone number"
            />
           </Grid>
           <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Street (optional)"
              name="street"
              value={formik.values.street}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter street address"
            />
           </Grid>
           <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="City (optional)"
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter city"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="State (optional)"
              name="state"
              value={formik.values.state}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter state"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Zipcode (optional)"
              name="zipcode"
              value={formik.values.zipcode}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter zipcode"
            />
            </Grid>
            {!editMode && (
              <>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Password *"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    style: { borderRadius: 10 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
               </Grid>
               <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Confirm Password *"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  InputProps={{
                    style: { borderRadius: 10 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
               </Grid>
              </>
            )}
          </Grid>
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            color="error"
            variant="outlined"
            onClick={() => handleClose("cancel")}
            startIcon={<CloseIcon />}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={loading}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {editMode ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddUserModal;